import { EMAIL_MAX_LENGTH, EMAIL_RE } from "@/lib/utils";

// MVP 2.0 #6 — CSV parser for the bulk-invite modal.
// UX-12 — now ALSO the composer's file-import parser, and the single home of
// the row cap + duplicate enrichment both surfaces share.
//
// Scope: spreadsheet exports carrying an email column and a name (or a
// first + last pair), in any column order, with or without a header row, with
// or without a BOM, delimited by comma / semicolon / tab. RFC 4180 quoted
// fields are supported, so `"Smith, Jr."` (embedded comma) or a field with
// embedded newlines / ""-escaped quotes parses correctly instead of splitting
// into extra columns.
//
// Server-side validation is the authoritative gate — this parser is best-
// effort for the preview table. Every row's `validation` is also re-checked
// by the backend, so a sloppy parse can't bypass server-side rules.

export type ParsedCsvRow = {
  row_index: number; // 0-based, post-header
  candidate_email: string;
  candidate_name: string;
  validation:
    | "valid"
    | "invalid_email"
    | "blank_name"
    | "blank_row"
    // Over the server's bounds. For a NAME this is not a per-row rejection
    // server-side: `candidate_name` (max 200) is a `Field(...)` constraint on
    // BulkInviteRow, as is the outer 320 on `candidate_email`, so one over-long
    // value 422s the ENTIRE request and the per-row partial-success contract
    // never runs (review-R6). An email is flagged from EMAIL_MAX_LENGTH (254)
    // up, which is where the server stops accepting it as an address at all. In the composer
    // that fires AFTER the irreversible create, leaving the recruiter on the
    // outcome screen with a raw 422 and a Retry that re-POSTs the identical
    // payload forever. Reachable from an ordinary Outlook paste, because
    // ANGLE_RE captures everything before `<` as the name.
    | "too_long";
};

// Server-side per-field bounds (backend/api/admin.py BulkInviteRow). Mirrored
// here so an over-long value is caught as a fixable ROW rather than a 422 that
// takes the whole batch down with it. Keep in lockstep with the Pydantic model.
export const MAX_CANDIDATE_NAME_CHARS = 200;
// FEAT-23: the limit every email field shares. It used to mirror the bulk
// row's outer 320, which let a 255–320 character address read "valid" here and
// then come back from the server as `invalid_email` — the real ceiling for an
// address is 254 everywhere.
export const MAX_CANDIDATE_EMAIL_CHARS = EMAIL_MAX_LENGTH;

// Mirrors the server's bulk cap (BulkInviteRequest max_length=200 /
// settings.invitation_bulk_max_rows). Surplus rows are DROPPED and reported so
// the UI can say so instead of a 422 after the round-trip — which in the
// composer arrives AFTER the irreversible create. Lives here, not in
// recipients.ts, so BOTH input doors (paste and file) enforce it and no caller
// can end up on an uncapped path.
export const MAX_RECIPIENT_ROWS = 200;

// Shared with the composer's paste parser (lib/recipients.ts) so "what counts
// as an email" can't drift between the CSV and paste inputs.
// Re-exported here so this module's callers are unchanged.
export { EMAIL_RE };
const BOM = "﻿";

export type ParsedCsv = {
  rows: ParsedCsvRow[];
  // Rows beyond MAX_RECIPIENT_ROWS that were cut. 0 in the normal case.
  dropped: number;
};

// ── Delimiter ──────────────────────────────────────────────────────────────

const DELIMITERS = [",", ";", "\t"] as const;

/**
 * Pick the delimiter from the first non-empty line, counting only occurrences
 * OUTSIDE quoted fields.
 *
 * Comma-only was wrong for a whole locale: Excel on a Polish (or German, or
 * French) machine writes `;` by default, so `Imie;Nazwisko;E-mail` tokenized
 * as ONE field per row and every candidate came back `invalid_email`. Ties go
 * to comma, which keeps every pre-existing file parsing exactly as before.
 */
function sniffDelimiter(text: string): string {
  const counts = new Map<string, number>(DELIMITERS.map((d) => [d, 0]));
  let inQuotes = false;
  let sawContent = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') i++;
        else inQuotes = false;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      sawContent = true;
      continue;
    }
    if (ch === "\n" || ch === "\r") {
      // Only stop once the line had something on it — a leading blank line
      // must not decide the delimiter for the whole file.
      if (sawContent) break;
      continue;
    }
    const seen = counts.get(ch);
    if (seen !== undefined) counts.set(ch, seen + 1);
    if (ch.trim().length > 0) sawContent = true;
  }
  let best = ",";
  let bestCount = 0;
  for (const d of DELIMITERS) {
    const n = counts.get(d) ?? 0;
    if (n > bestCount) {
      best = d;
      bestCount = n;
    }
  }
  return best;
}

// RFC 4180 tokenizer — splits CSV text into rows of fields in a single pass,
// honoring double-quoted fields (which may contain the delimiter, CR/LF, and
// ""-escaped quotes). Unquoted fields run to the next delimiter / row break.
function tokenizeCsv(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  let sawField = false; // distinguishes a real trailing row from a clean EOF
  const pushField = () => {
    row.push(field);
    field = "";
    sawField = false;
  };
  const pushRow = () => {
    pushField();
    rows.push(row);
    row = [];
  };
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      sawField = true;
    } else if (ch === delimiter) {
      pushField();
      sawField = true; // a delimiter implies a following (possibly empty) field
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      pushRow();
    } else {
      field += ch;
      sawField = true;
    }
  }
  // Flush a final unterminated row (file without a trailing newline).
  if (sawField || field.length > 0 || row.length > 0) pushRow();
  return rows;
}

// ── Header mapping ─────────────────────────────────────────────────────────

type HeaderRole = "email" | "name" | "first" | "last";

/** Fold a header cell to bare lowercase letters+digits: "Adres e-mail" -> "adresemail", "Imię" -> "imie". */
function normalizeHeader(cell: string): string {
  return cell
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // combining marks: NFD "e+ogonek" -> "e"
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

// Substring probes, checked in the order below. Order is load-bearing:
// "firstname" contains "name", and the full-name compounds contain "imie".
// Within one list a token is a SUBSTRING probe, so a longer entry that
// contains a shorter one in the same list can never fire ("emailaddress"
// and "email" both contain "mail"): the lists keep only the shortest
// probe of each family (SIMP-23).
const EMAIL_TOKENS = ["mail"];
const FULLNAME_TOKENS = [
  "fullname",
  "candidatename",
  "imieinazwisko",
  "imienazwisko",
];
const FIRST_TOKENS = ["forename", "first", "given", "imie"];
const LAST_TOKENS = ["surname", "last", "family", "nazwisko"];
const NAME_TOKENS = ["name", "candidate", "osoba"];

function classifyHeader(cell: string): HeaderRole | null {
  const n = normalizeHeader(cell);
  if (!n) return null;
  if (EMAIL_TOKENS.some((t) => n.includes(t))) return "email";
  if (FULLNAME_TOKENS.some((t) => n.includes(t))) return "name";
  if (FIRST_TOKENS.some((t) => n.includes(t))) return "first";
  if (LAST_TOKENS.some((t) => n.includes(t))) return "last";
  if (NAME_TOKENS.some((t) => n.includes(t))) return "name";
  return null;
}

/**
 * A first row is a header iff something in it reads as an email COLUMN and
 * nothing in it carries an "@".
 *
 * The old rule needed both an "email" and a "name" token, so the ordinary ATS
 * export `First name,Last name,E-mail` failed detection twice over — "E-mail"
 * does not contain the substring "email", and there is no "name"-only column —
 * and the parser silently fell back to col0=email, col1=name.
 *
 * The "@" test replaces an EMAIL_RE test, which it subsumes (every valid
 * address contains one) and which was too weak in exactly one reachable case:
 * a MALFORMED address passes EMAIL_RE's rejection while `normalizeHeader`
 * folds it to a header-ish token. `ada@gmail` (no TLD) becomes "adagmail",
 * which contains "mail" — so the first candidate of a HEADERLESS file was
 * eaten as the header. That cost two things silently: the row itself, with no
 * message and no counter, and — because the fake header maps no name column —
 * every REMAINING row's real name, re-derived from its address instead. No
 * genuine header cell contains "@"; a data row's address cell always does.
 */
function looksLikeHeader(cells: string[]): boolean {
  if (!cells.some((c) => classifyHeader(c) === "email")) return false;
  return !cells.some((c) => c.includes("@"));
}

type ColumnMap = {
  emailCol: number;
  nameCol: number;
  firstCol: number;
  lastCol: number;
};

const COLUMN_FOR_ROLE: Record<HeaderRole, keyof ColumnMap> = {
  email: "emailCol",
  name: "nameCol",
  first: "firstCol",
  last: "lastCol",
};

function mapHeaderColumns(cells: string[]): ColumnMap {
  const map: ColumnMap = {
    emailCol: -1,
    nameCol: -1,
    firstCol: -1,
    lastCol: -1,
  };
  // First header per role wins (SIMP-43: a keyed table, not a four-arm
  // ladder over the role enum).
  cells.forEach((cell, i) => {
    const role = classifyHeader(cell);
    const col = role === null ? null : COLUMN_FOR_ROLE[role];
    if (col && map[col] < 0) map[col] = i;
  });
  if (map.emailCol < 0) map.emailCol = 0;
  return map;
}

/**
 * Headerless fallback: find the column that actually holds an address rather
 * than assuming `email,name`. `Ada Lovelace,ada@example.com` used to parse as
 * email="ada lovelace", and the whole file came back invalid.
 */
function probeColumns(cells: string[]): ColumnMap {
  // Cells arrive trimmed (`parseCsv` trims the whole grid once, SIMP-42).
  let emailCol = cells.findIndex((c) => EMAIL_RE.test(c.toLowerCase()));
  if (emailCol < 0) emailCol = 0;
  let nameCol = cells.findIndex((c, i) => i !== emailCol && c.length > 0);
  if (nameCol < 0) nameCol = emailCol === 0 ? 1 : 0;
  return { emailCol, nameCol, firstCol: -1, lastCol: -1 };
}

// A name must be a single line. An RFC-4180 quoted cell can legitimately carry
// embedded newlines/tabs (Excel Alt+Enter, a wrapped ATS export cell), which
// `parseCsv` preserves. Left intact it reaches the candidate's greeting whole —
// the send path only `.trim()`s the ends — and it also breaks the "Paste or
// edit the list" dialog, whose `parseRecipients` splits on `\n` before it
// tokenizes quotes and would re-split the one candidate into a phantom
// `invalid_email` row plus a wrong-name row.
//
// `makeRecipientRow` collapses on the same grounds for rows that never touch a
// CSV. Two doors, ONE rule, one function: this collapse is what every door
// applies — the file parser here, the paste parser's `makeRow`, the inline
// editor and the send (lib/recipientList.ts re-exports it). Until night-p9
// (2026-10-08) the paste door only trimmed and this parser kept its own copy
// (SIMP-11).
export function normalizeRecipientName(name: string): string {
  return name.replace(/\s+/g, " ").trim();
}

function pickName(cells: string[], cols: ColumnMap): string {
  if (cols.nameCol >= 0) {
    const single = normalizeRecipientName(cells[cols.nameCol] ?? "");
    if (single) return single;
  }
  return normalizeRecipientName(
    [cols.firstCol, cols.lastCol]
      .filter((i) => i >= 0)
      .map((i) => cells[i] ?? "")
      .filter(Boolean)
      .join(" "),
  );
}

// The ONE verdict ladder for a recipient row — the CSV door, the paste door
// and the inline editor (lib/recipients.ts re-exports it) all judge a row
// here: invalid_email -> blank_name -> too_long. Length LAST, so a row that
// is both malformed and over-long reports the more actionable problem. It
// lives beside the bounds it reads; until night-p9 (2026-10-08) `classifyRow`
// below was a hand copy "mirroring it exactly", the drift SIMP-18 named.
export function validateRecipient(
  email: string,
  name: string,
): ParsedCsvRow["validation"] {
  if (!EMAIL_RE.test(email.trim().toLowerCase())) return "invalid_email";
  if (!name.trim()) return "blank_name";
  // These are the server's per-field bounds: exceeding either 422s the whole
  // batch instead of rejecting the one row (review-R6), which in the composer
  // happens after the create is already committed.
  if (email.trim().length > MAX_CANDIDATE_EMAIL_CHARS) return "too_long";
  if (name.trim().length > MAX_CANDIDATE_NAME_CHARS) return "too_long";
  return "valid";
}

// A CSV row adds one verdict of its own — an entirely empty line — and then
// delegates. `parseCsv` already trimmed and lowercased, so the ladder's own
// trims are no-ops here.
function classifyRow(email: string, name: string): ParsedCsvRow["validation"] {
  if (!email && !name) return "blank_row";
  return validateRecipient(email, name);
}

export function parseCsv(text: string): ParsedCsv {
  // Strip UTF-8 BOM. Excel exports as UTF-8-with-BOM by default on Windows.
  const stripped = text.startsWith(BOM) ? text.slice(BOM.length) : text;
  const grid = tokenizeCsv(stripped, sniffDelimiter(stripped))
    .map((cells) => cells.map((c) => c.trim()))
    .filter((cells) => cells.some((c) => c.length > 0));
  if (grid.length === 0) return { rows: [], dropped: 0 };

  const hasHeader = looksLikeHeader(grid[0]);
  const dataRows = hasHeader ? grid.slice(1) : grid;
  if (dataRows.length === 0) return { rows: [], dropped: 0 };
  const cols = hasHeader ? mapHeaderColumns(grid[0]) : probeColumns(dataRows[0]);

  // Build every data row, DROP the ones carrying neither an email nor a name,
  // and only THEN apply the cap. That order is load-bearing.
  //
  // The blank filter used to live in the caller (handleFileChange), which runs
  // AFTER this function has already sliced at MAX_RECIPIENT_ROWS — so a junk
  // line consumed one of the 200 slots and was then thrown away. A blank row is
  // not an empty line (the grid filter above already removed those): it is a
  // row whose mapped email AND name cells are empty while some other column is
  // populated, i.e. an export line carrying only a phone number or a date.
  // Measured: a 250-row file of 60 such lines + 190 real candidates imported
  // 140 people, and the recruiter was told the 200-per-send limit forced the
  // cut while the table sat 60 short of it. Filtering first spends the budget
  // on rows that can actually become recipients, and makes `dropped` mean only
  // "real candidates the cap left out".
  const all: ParsedCsvRow[] = dataRows
    .map((cells) => {
      const email = (cells[cols.emailCol] ?? "").trim().toLowerCase();
      const name = pickName(cells, cols);
      return {
        row_index: 0, // positional — assigned below, once blanks are gone
        candidate_email: email,
        candidate_name: name,
        validation: classifyRow(email, name),
      };
    })
    .filter((r) => r.validation !== "blank_row")
    .map((r, idx) => ({ ...r, row_index: idx }));

  return capRows(all);
}

/**
 * The row cap, applied ONCE for every parser: the first MAX_RECIPIENT_ROWS
 * rows and how many were cut. The constant lived here already; the slice
 * was still written out at both doors (SIMP-10). `appendCapped` in
 * lib/recipientList.ts is the LIST-level cap (room left beside existing
 * rows), a different question.
 */
export function capRows<T>(rows: T[]): { rows: T[]; dropped: number } {
  if (rows.length <= MAX_RECIPIENT_ROWS) return { rows, dropped: 0 };
  return {
    rows: rows.slice(0, MAX_RECIPIENT_ROWS),
    dropped: rows.length - MAX_RECIPIENT_ROWS,
  };
}

// ── Duplicate enrichment ───────────────────────────────────────────────────
//
// Moved here from BulkInviteModal.tsx (UX-12): it is a pure function, it was
// already being imported OUT of a component module by useComposer, and the
// file importer makes a third consumer.

// Parsed CSV row plus intra-batch duplicate-detection state.
// `is_duplicate`: this email appears more than once in the batch.
// `first_occurrence_row_index` / `last_occurrence_row_index`: present only
//   for duplicate rows so the UI can render "row N wins" / "duplicate of row M"
//   regardless of which side of the group this row is on.
// `will_send` is `true` iff this row will be POSTed to /invite-bulk under
//   the current `keepLastOnly` setting.
export type EnrichedCsvRow = ParsedCsvRow & {
  is_duplicate: boolean;
  first_occurrence_row_index: number | null;
  last_occurrence_row_index: number | null;
  will_send: boolean;
};

/**
 * Annotate parsed CSV rows with intra-batch duplicate state.
 *
 * The recruiter sees this output in the Review phase + it drives the actual
 * /invite-bulk payload. Pure function, depends only on its inputs.
 *
 * When `keepLastOnly` is true (default), earlier occurrences of a repeated
 * email are flagged `will_send: false`, matching the backend UPSERT's
 * "last write wins" semantics. This prevents the dead-link footgun where
 * the first email's token gets rotated before the candidate clicks it.
 *
 * When false, every valid row is sent — the backend will still UPSERT,
 * so the dead-link problem returns, but the recruiter has consented.
 */
export function enrichCsvRows(
  parsedRows: ParsedCsvRow[],
  keepLastOnly: boolean,
): EnrichedCsvRow[] {
  const firstSeen = new Map<string, number>();
  const lastSeen = new Map<string, number>();
  const occurrences = new Map<string, number>();
  parsedRows.forEach((r) => {
    if (r.validation !== "valid") return;
    const email = r.candidate_email; // already lowercased+trimmed by parseCsv
    if (!firstSeen.has(email)) firstSeen.set(email, r.row_index);
    lastSeen.set(email, r.row_index);
    occurrences.set(email, (occurrences.get(email) ?? 0) + 1);
  });
  return parsedRows.map((r) => {
    if (r.validation !== "valid") {
      return {
        ...r,
        is_duplicate: false,
        first_occurrence_row_index: null,
        last_occurrence_row_index: null,
        will_send: false,
      };
    }
    const isDuplicate = (occurrences.get(r.candidate_email) ?? 0) > 1;
    const first = firstSeen.get(r.candidate_email) ?? r.row_index;
    const last = lastSeen.get(r.candidate_email) ?? r.row_index;
    const isLatest = r.row_index === last;
    return {
      ...r,
      is_duplicate: isDuplicate,
      first_occurrence_row_index: isDuplicate ? first : null,
      last_occurrence_row_index: isDuplicate ? last : null,
      will_send: keepLastOnly ? isLatest : true,
    };
  });
}
